import React, { useEffect, useMemo, useState } from "react"
import { Modal, Input, Checkbox, Button, message, Spin, Alert } from "antd"
import { translations } from "../../utils/Translation/staticTranslation"
import { getModuleDisplayLabel } from "../../utils/createRolesHelpers"
import { apiAccessKeyToUiAccess } from "../../Apis/admin-roles"
import "../../styles/Pages/create-roles.css"

function emptyAccessMap(permissionRows) {
  if (!permissionRows?.length) return {}
  return Object.fromEntries(
    permissionRows.map((r) => [r.accessKey, "none"]),
  )
}

function mergeAccessFromRole(saved, permissionRows) {
  const base = emptyAccessMap(permissionRows)
  if (!saved || typeof saved !== "object") return base
  return { ...base, ...saved }
}

const CreateRoleModal = ({
  open,
  onClose,
  onSave,
  editingRole = null,
  permissionRows = null,
  moduleAccessTypes = null,
  modulesLoading = false,
  modulesLoadError = false,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.createRolesPage || {}

  const [roleName, setRoleName] = useState("")
  const [description, setDescription] = useState("")
  const [accessByModule, setAccessByModule] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const accessTypeColumns = useMemo(() => {
    const fallback = [
      { key: "read_only", label: t.partial_access },
      { key: "edit_only", label: t.edit_only_access },
      { key: "full_access", label: t.full_access },
    ]
    if (!moduleAccessTypes?.length) return fallback
    const labelByUi = {
      full: t.full_access,
      edit: t.edit_only_access,
      partial: t.partial_access,
    }
    return moduleAccessTypes.map((col) => ({
      ...col,
      label: labelByUi[apiAccessKeyToUiAccess(col.key)] ?? col.label,
    }))
  }, [moduleAccessTypes, t.partial_access, t.edit_only_access, t.full_access])

  const isEdit = Boolean(editingRole?.id)
  const rowsReady = Boolean(permissionRows?.length)
  const canSubmit = rowsReady && !modulesLoading && !modulesLoadError

  useEffect(() => {
    if (!open) return
    if (modulesLoading) return
    if (!permissionRows?.length) return
    if (editingRole?.id) {
      setRoleName(editingRole.name || "")
      setDescription(editingRole.description || "")
      setAccessByModule(
        mergeAccessFromRole(editingRole.accessByModule, permissionRows),
      )
    } else {
      setRoleName("")
      setDescription("")
      setAccessByModule(emptyAccessMap(permissionRows))
    }
  }, [open, editingRole, modulesLoading, permissionRows])

  const setRowAccess = (accessKey, mode) => {
    setAccessByModule((prev) => ({ ...prev, [accessKey]: mode }))
  }

  const handleAccessTypeChange = (moduleAccessKey, apiAccessTypeKey, checked) => {
    const targetUi = apiAccessKeyToUiAccess(apiAccessTypeKey)
    if (targetUi === "none") return
    if (checked) {
      setRowAccess(moduleAccessKey, targetUi)
      return
    }
    setAccessByModule((prev) => {
      const cur = prev[moduleAccessKey] || "none"
      if (cur !== targetUi) return prev
      return { ...prev, [moduleAccessKey]: "none" }
    })
  }

  const handleSubmit = async () => {
    if (!canSubmit) {
      message.error(t.error_modules_load_failed)
      return
    }
    const name = roleName.trim()
    if (!name) {
      message.error(t.validation_role_name)
      return
    }
    setSubmitting(true)
    try {
      await Promise.resolve(
        onSave({
          id: editingRole?.id,
          name,
          description: description.trim(),
          accessByModule: { ...accessByModule },
        }),
      )
      onClose()
    } catch {
      // Parent API failed — keep modal open
    } finally {
      setSubmitting(false)
    }
  }

  const isRTL = lang === "ar"

  return (
    <Modal
      title={isEdit ? t.modal_title_edit_role : t.modal_title_create_role}
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnClose
      footer={null}
      className="create-role-modal"
      maskClosable={!submitting}
      closable={!submitting}
    >
      <div dir={isRTL ? "rtl" : "ltr"}>
        <div className="create-role-modal-field">
          <label className="create-role-modal-label" htmlFor="create-role-name-input">
            {t.role_name_label}
          </label>
          <Input
            id="create-role-name-input"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder={t.role_name_placeholder}
            maxLength={120}
          />
        </div>

        <div className="create-role-modal-field create-role-modal-field--counted">
          <label
            className="create-role-modal-label"
            htmlFor="create-role-description-input"
          >
            {t.role_description_label}
          </label>
          <Input.TextArea
            id="create-role-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t.role_description_placeholder}
            rows={3}
            maxLength={500}
            showCount
          />
        </div>

        {modulesLoading ? (
          <div className="create-role-perm-loading">
            <Spin size="large" />
          </div>
        ) : modulesLoadError ? (
          <Alert
            type="error"
            showIcon
            message={t.error_modules_load_failed}
            className="create-role-perm-alert"
          />
        ) : !permissionRows?.length ? (
          <Alert
            type="warning"
            showIcon
            message={t.warning_modules_empty}
            description={t.modal_modules_empty_hint}
            className="create-role-perm-alert"
          />
        ) : (
          <div className="create-role-perm-table-wrap">
            <div className="create-role-perm-head">
              <div className="create-role-perm-head-spacer" aria-hidden="true" />
              {accessTypeColumns.map((col) => (
                <span
                  key={col.key}
                  className="create-role-perm-head-check"
                  title={col.label}
                >
                  {col.label}
                </span>
              ))}
            </div>
            <div className="create-role-perm-body">
              {permissionRows.map((row) => {
                const access = accessByModule[row.accessKey] || "none"
                return (
                  <div key={row.accessKey} className="create-role-perm-row">
                    <span className="create-role-perm-row-label">
                      {getModuleDisplayLabel(row.accessKey, row.label, lang)}
                    </span>
                    {accessTypeColumns.map((col) => {
                      const colUi = apiAccessKeyToUiAccess(col.key)
                      const checked =
                        colUi !== "none" && access === colUi
                      return (
                        <span
                          key={col.key}
                          className="create-role-perm-row-check"
                        >
                          <Checkbox
                            checked={checked}
                            onChange={(e) =>
                              handleAccessTypeChange(
                                row.accessKey,
                                col.key,
                                e.target.checked,
                              )
                            }
                          />
                        </span>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div className="create-role-modal-footer">
          <Button onClick={onClose} disabled={submitting}>
            {t.cancel}
          </Button>
          <Button
            className="create-roles-action-btn"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            loading={submitting}
          >
            {isEdit ? t.submit_update_role : t.submit_create}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default CreateRoleModal